import ja from "date-fns/locale/ja";
import { FormControl, TextField } from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { SizingWrapper, type ISizingWrapperProps } from "../Wrapper";
import { Label } from "../Label";

interface IProps extends ISizingWrapperProps {
  label: string;
  displayLabel?: boolean;
  value: null | Date;
  onChange: (value: Date | null) => void;
  onErrorChange?: (error: unknown) => void;
  views?: Array<"year" | "month" | "day">;
  maxDate?: Date;
  minDate?: Date;
  disableFuture?: boolean;
  disablePast?: boolean;
  defaultCalendarMonth?: Date;
  inputFormat?: string;
  mask?: string;
  openTo?: "day" | "month" | "year";
  disabled?: boolean;
  error?: string;
  required?: boolean;
  /** MUI TextFieldの表示密度。省略時は従来どおり "medium" */
  muiSize?: "small" | "medium";
}

const DatePicker: React.FC<IProps> = (props) => {
  const {
    label,
    displayLabel = false,
    value,
    onChange,
    onErrorChange,
    views,
    maxDate = undefined,
    minDate = undefined,
    disableFuture = false,
    disablePast = false,
    defaultCalendarMonth,
    size,
    inputFormat,
    mask,
    openTo = "day",
    disabled,
    error,
    required = false,
    muiSize,
  } = props;

  const defaultErrorText = "日付の形式が正しくありません";
  const normalizedErrorText =
    error === "Invalid date" ? defaultErrorText : error;
  const resolvedOpenTo =
    views !== undefined && views.length > 0 && !views.includes(openTo)
      ? views[0]
      : openTo;

  return (
    <SizingWrapper size={size}>
      {displayLabel && <Label required={required}>{label}</Label>}
      <FormControl fullWidth>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={ja}
          dateFormats={{ monthAndYear: "yyyy/MM", year: "yyyy" }}
          localeText={{
            previousMonth: "前月を表示", // < のツールチップ
            nextMonth: "次月を表示", // > のツールチップ
            cancelButtonLabel: "キャンセル", // スマホ画面のCANCELボタン
            okButtonLabel: "選択", // スマホ画面のOKボタン
          }}
        >
          <MuiDatePicker
            // TODO: iconを設置して、Clearableにする
            // issueにて議論されているので実装されるかも(https://github.com/mui/mui-x/issues/4450)
            // TODO: 逆にTextFieldはClearできなくていい
            label={label}
            value={value}
            views={views}
            defaultCalendarMonth={defaultCalendarMonth}
            onChange={onChange}
            onError={(error) => {
              onErrorChange?.(error);
            }}
            renderInput={(params) => {
              const showInternalValidationError =
                onErrorChange === undefined && params.error === true;
              return (
                <TextField
                  {...params}
                  size={muiSize}
                  error={
                    Boolean(normalizedErrorText) || showInternalValidationError
                  }
                  helperText={
                    normalizedErrorText ??
                    (showInternalValidationError ? defaultErrorText : null)
                  }
                />
              );
            }}
            inputFormat={inputFormat ?? "yyyy/MM/dd"}
            mask={mask ?? "____/__/__"}
            disableFuture={disableFuture}
            disablePast={disablePast}
            maxDate={maxDate}
            minDate={minDate}
            openTo={resolvedOpenTo}
            disabled={disabled}
            // SP のダイアログは背景の誤タップで閉じない（キャンセル/選択ボタンでのみ閉じる）
            DialogProps={{ onClose: () => undefined }}
          />
        </LocalizationProvider>
      </FormControl>
    </SizingWrapper>
  );
};

export { DatePicker };
